import { toast } from "@/components/ui/use-toast";
import { animatePercentage } from "@/libs/utils";
import { usePercentageStore } from "@/store/store";
import { useCallback, useState } from "react";
interface PostDataOptions<T> {
  url: string;
  data: T;
}
interface PostDataResponse {
  success: boolean;
  message?: string;
  data?: any;
}
interface UsePostDataResult<T> {
  postData: (options: PostDataOptions<T>) => Promise<any>;
  loading: boolean;
  error?: string;
  response?: PostDataResponse;
}
function usePostData<T>(): UsePostDataResult<T> {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [response, setResponse] = useState<PostDataResponse | undefined>(
    undefined
  );
  const setPercentage = usePercentageStore((s) => s.setPercentage);
  const resetPercentage = usePercentageStore((s) => s.resetPercentage);
  const postData = useCallback(
    async ({ url, data }: PostDataOptions<T>): Promise<any> => {
      setLoading(true);
      setError(undefined);
      setResponse(undefined);
      const token = localStorage.getItem("jwtToken");
      try {
        // Démarrer l'animation du pourcentage
        animatePercentage(100, setPercentage);
        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
        const res = await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        });
        const result = await res.json();
        if (res.status === 400) {
          const message = result.error.violations[0].title;
          toast({
            className: "text-red-500",
            title: message,
          });
        }
        if (
          res.status === 422 &&
          Array.isArray(result) &&
          Array.isArray(result[0]) &&
          result[0].length === 2
        ) {
          toast({
            className: "text-red-500",
            title: result[0][1],
          });
        }
        if (!res.ok) {
          throw new Error(result.message || "Something went wrong");
        }
        setResponse({
          success: true,
          message: result.message,
          data: result,
        });
        setPercentage(100); // S'assurer que l'animation atteint 100%.
        setTimeout(() => {
          resetPercentage();
        }, 2500); // Réinitialiser après une seconde.

        return result;
      } catch (err: any) {
        setError(err.message || "An unknown error occurred");
        setResponse({
          success: false,
          message: err.message,
        });
        return null;
      } finally {
        setLoading(false);
        setPercentage(100); // S'assurer que l'animation atteint 100%.
        setTimeout(() => resetPercentage(), 2500); // Réinitialiser après une seconde.
      }
    },
    [setPercentage, resetPercentage]
  );
  return { postData, loading, error, response };
}
export default usePostData;
